import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ApiError,
  AuthenticatedUser,
  clearAuthToken,
  login as loginRequest,
  RoleSlug,
  setAuthToken,
} from '@/services/api';

export type Role = RoleSlug;

const AUTH_STORAGE_KEY = 'lkcmedical:session';

export const ROLE_DETAILS: Record<
  Role,
  { label: string; description: string; accent: string; surface: string }
> = {
  medical_director: {
    label: '醫療總監',
    description: '全面掌握系統狀態',
    accent: 'text-brand-indigo',
    surface: 'bg-indigo-50 text-indigo-700',
  },
  surgery: {
    label: '外科主管',
    description: '管理手術案例',
    accent: 'text-rose-500',
    surface: 'bg-rose-50 text-rose-700',
  },
  internal_med: {
    label: '內科主治',
    description: '處理複雜病例',
    accent: 'text-emerald-600',
    surface: 'bg-emerald-50 text-emerald-700',
  },
  customer_service: {
    label: '客服櫃檯',
    description: '排程與接待',
    accent: 'text-sky-600',
    surface: 'bg-sky-50 text-sky-700',
  },
  billing: {
    label: '營收管理',
    description: '帳務與稽核流程',
    accent: 'text-amber-600',
    surface: 'bg-amber-50 text-amber-800',
  },
};

export const ROLE_PERMISSIONS: Record<Role, string[]> = {
  medical_director: ['notes:write', 'billing:approve', 'billing:remind', 'schedule:modify', 'audit:view', 'care:signoff', 'care:handoff'],
  surgery: ['notes:write', 'schedule:modify', 'care:update', 'handoff:create', 'care:signoff', 'care:handoff'],
  internal_med: ['notes:write', 'care:update', 'handoff:create', 'care:signoff', 'care:handoff'],
  customer_service: ['schedule:view', 'billing:remind', 'notify:send'],
  billing: ['billing:approve', 'billing:remind', 'audit:view', 'quote:create', 'notify:send'],
};

type AuthStatus = 'initializing' | 'idle' | 'loading' | 'authenticated';

interface AuthState {
  user: AuthenticatedUser | null;
  token: string | null;
  status: AuthStatus;
  error: string | null;
}

interface AuthContextValue {
  user: AuthenticatedUser | null;
  token: string | null;
  role: Role | null;
  roleMeta: (typeof ROLE_DETAILS)[Role] | null;
  status: AuthStatus;
  error: string | null;
  isAuthenticated: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readPersistedSession = (): { user: AuthenticatedUser; token: string } | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { user: AuthenticatedUser; token: string };
    if (parsed?.token && parsed?.user) {
      return parsed;
    }
  } catch (error) {
    console.warn('Unable to parse stored session', error);
  }
  return null;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    status: 'initializing',
    error: null,
  });

  useEffect(() => {
    const stored = readPersistedSession();
    if (stored) {
      setAuthToken(stored.token);
      setState({ user: stored.user, token: stored.token, status: 'authenticated', error: null });
    } else {
      setState((prev) => ({ ...prev, status: 'idle' }));
    }
  }, []);

  const persistSession = useCallback((payload: { user: AuthenticatedUser; token: string }) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
    }
  }, []);

  const clearSession = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, []);

  const login = useCallback(
    async (credentials: { email: string; password: string }) => {
      setState((prev) => ({ ...prev, status: 'loading', error: null }));
      try {
        const response = await loginRequest(credentials);
        setAuthToken(response.token);
        persistSession(response);
        setState({ user: response.user, token: response.token, status: 'authenticated', error: null });
      } catch (error) {
        const message =
          error instanceof ApiError && error.status === 401
            ? '電子郵件或密碼不正確。'
            : '目前無法登入，請稍後再試。';
        setState((prev) => ({ ...prev, status: 'idle', error: message }));
        throw error;
      }
    },
    [persistSession],
  );

  const logout = useCallback(() => {
    clearAuthToken();
    clearSession();
    setState({ user: null, token: null, status: 'idle', error: null });
  }, [clearSession]);

  const role = state.user?.role ?? null;
  const roleMeta = role ? ROLE_DETAILS[role] : null;
  const isAuthenticated = state.status === 'authenticated' && !!state.user && !!state.token;

  const hasPermission = useCallback(
    (permission: string) => {
      if (!role) return false;
      return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
    },
    [role],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user: state.user,
      token: state.token,
      role,
      roleMeta,
      status: state.status,
      error: state.error,
      isAuthenticated,
      login,
      logout,
      hasPermission,
    }),
    [hasPermission, isAuthenticated, login, logout, role, roleMeta, state.error, state.status, state.token, state.user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
